import { useApolloClient } from "@apollo/client"
import tenantQuery from "@/v2-backoffice-client/tenant/tenant.query.gql"
import customerQuery from "@/v2-backoffice-client/customer/customer.query.gql"
import type {
  TenantQuery,
  TenantQueryVariables,
} from "@/v2-backoffice-client/tenant/__types__/tenant.query"
import type {
  CustomerQuery,
  CustomerQueryVariables,
} from "@/v2-backoffice-client/customer/__types__/customer.query"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { IS_DELETED_COLUMN } from "@/v2-backoffice-client/tenant/tenant.constants"

/** One parsed spreadsheet line. */
export type ImportedInvitee = {
  email: string
  firstName: string
  lastName: string
  teamName: string
}

export type InvitationImportParse = {
  invitees: ImportedInvitee[]
  /** Human-readable skip reasons ("line 4: no email"), for the toast. */
  issues: string[]
}

/** How one distinct pasted team name resolved against existing data. */
export type ImportedTeamResolution = {
  teamName: string
  /** Exactly one existing team matched by name. */
  tenant: TenantFragment | null
  /** Several existing teams share the name — the operator picks. */
  candidates: TenantFragment[]
  /** For brand-new teams: the customer whose name matches the team's. */
  suggestedCustomer: CustomerFragment | null
}

// Matching is exact on the normalized (trimmed, lowercased, unquoted) cell,
// so unrecognized headers never mis-map — they just don't map. The HubSpot
// entries mirror that CRM's contact-export column labels in English and
// Norwegian portals, so a raw "Export view" paste maps without editing;
// the association column (the company name) feeds the team matcher.
const HEADER_ALIASES: Record<keyof ImportedInvitee, string[]> = {
  email: [
    "email",
    "e-mail",
    "epost",
    "e-post",
    "e-postadresse",
    "mail",
    "email address",
  ],
  firstName: ["first name", "firstname", "first", "fornavn", "given name"],
  lastName: ["last name", "lastname", "last", "etternavn", "surname"],
  teamName: [
    "team",
    "tenant",
    "company",
    "firma",
    "selskap",
    "kunde",
    "org",
    // HubSpot contact exports (EN portal)
    "company name",
    "primary company",
    "associated company",
    "primary associated company",
    // HubSpot contact exports (NO portal)
    "firmanavn",
    "primært selskap",
    "tilknyttet selskap",
    "primært tilknyttet selskap",
  ],
}

function detectDelimiter(line: string): string {
  for (const delimiter of ["\t", ";", ","]) {
    if (line.includes(delimiter)) return delimiter
  }
  return "\t"
}

function matchHeader(cell: string): keyof ImportedInvitee | null {
  const normalized = cell.trim().toLowerCase()
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.includes(normalized)) return field as keyof ImportedInvitee
  }
  return null
}

/**
 * Splits one line on the delimiter, honouring CSV double-quoting — raw
 * CSV file pastes (HubSpot exports included) quote their fields, and a
 * company like "Hansen Eiendom, avd Oslo" must not shift the columns.
 * A doubled quote inside a quoted field is the CSV escape for a literal
 * quote. Tab-separated spreadsheet pastes have no quoting and pass
 * through unchanged.
 */
function splitLine(line: string, delimiter: string): string[] {
  const cells: string[] = []
  let current = ""
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += char
      }
    } else if (char === '"' && current.trim() === "") {
      inQuotes = true
    } else if (char === delimiter) {
      cells.push(current.trim())
      current = ""
    } else {
      current += char
    }
  }
  cells.push(current.trim())
  return cells
}

/**
 * Parses a pasted spreadsheet into invitees. Accepts tab, semicolon, or
 * comma separated rows; with a header row the columns map by name (English
 * and Norwegian aliases), without one they read positionally as
 * email · first name · last name · team. Lines without a plausible email
 * are skipped and reported.
 */
export function parseInvitationImport(text: string): InvitationImportParse {
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line.trim().length > 0)
  if (lines.length === 0) return { invitees: [], issues: ["nothing to parse"] }

  // Excel's "sep=;" first-line hint (some hand-made CSVs carry it): it
  // names the delimiter and is not data.
  let forcedDelimiter: string | null = null
  const sepMatch = /^sep=(.)$/i.exec(lines[0].trim())
  if (sepMatch) {
    forcedDelimiter = sepMatch[1]
    lines.shift()
    if (lines.length === 0)
      return { invitees: [], issues: ["nothing to parse"] }
  }

  const delimiter = forcedDelimiter ?? detectDelimiter(lines[0])
  const grid = lines.map((line) => splitLine(line, delimiter))

  // Header-aware column mapping: a first row without any @ that names at
  // least the email column is a header.
  let columnOf: Partial<Record<keyof ImportedInvitee, number>> = {
    email: 0,
    firstName: 1,
    lastName: 2,
    teamName: 3,
  }
  let startRow = 0
  const first = grid[0]
  if (!first.some((cell) => cell.includes("@"))) {
    const mapped: Partial<Record<keyof ImportedInvitee, number>> = {}
    first.forEach((cell, index) => {
      const field = matchHeader(cell)
      if (field && mapped[field] === undefined) mapped[field] = index
    })
    if (mapped.email !== undefined) {
      columnOf = mapped
      startRow = 1
    }
  }

  const invitees: ImportedInvitee[] = []
  const issues: string[] = []
  for (let i = startRow; i < grid.length; i++) {
    const cells = grid[i]
    const email = cells[columnOf.email ?? 0] ?? ""
    if (!email.includes("@")) {
      issues.push(`line ${i + 1}: no email — skipped`)
      continue
    }
    invitees.push({
      email,
      firstName:
        columnOf.firstName !== undefined
          ? (cells[columnOf.firstName] ?? "")
          : "",
      lastName:
        columnOf.lastName !== undefined ? (cells[columnOf.lastName] ?? "") : "",
      teamName:
        columnOf.teamName !== undefined ? (cells[columnOf.teamName] ?? "") : "",
    })
  }
  return { invitees, issues }
}

/**
 * Resolves the distinct pasted team names in two batched lookups: existing
 * teams by exact name (one match auto-selects, several ask the operator),
 * and for names with no team, customers by the same name — teams and their
 * customers usually share it — so "create + link" is one click. Rows
 * without a team name group under "" and always need a manual pick.
 */
export function useImportedTeamResolver() {
  const client = useApolloClient()

  return async function resolveTeams(
    teamNames: string[],
  ): Promise<ImportedTeamResolution[]> {
    const distinct = [...new Set(teamNames.map((n) => n.trim()))]
    const named = distinct.filter((n) => n.length > 0)

    const tenantsByName = new Map<string, TenantFragment[]>()
    const customersByName = new Map<string, CustomerFragment[]>()

    if (named.length > 0) {
      const tenantsRes = await client.query<TenantQuery, TenantQueryVariables>({
        query: tenantQuery,
        fetchPolicy: "network-only",
        variables: {
          input: {
            limit: named.length * 5,
            filters: [
              { column: "Name", in: named },
              { column: IS_DELETED_COLUMN, eq: "0" },
            ],
          },
        },
      })
      for (const tenant of tenantsRes.data.getTenants.items) {
        const key = tenant.Name.trim().toLowerCase()
        tenantsByName.set(key, [...(tenantsByName.get(key) ?? []), tenant])
      }

      const unmatched = named.filter(
        (name) => !tenantsByName.has(name.toLowerCase()),
      )
      if (unmatched.length > 0) {
        const customersRes = await client.query<
          CustomerQuery,
          CustomerQueryVariables
        >({
          query: customerQuery,
          fetchPolicy: "network-only",
          variables: {
            input: {
              limit: unmatched.length * 5,
              filters: [{ column: "Name", in: unmatched }],
            },
          },
        })
        for (const customer of customersRes.data.getCustomers.items) {
          const key = (customer.Name ?? "").trim().toLowerCase()
          customersByName.set(key, [
            ...(customersByName.get(key) ?? []),
            customer,
          ])
        }
      }
    }

    return distinct.map((teamName) => {
      const key = teamName.toLowerCase()
      const tenants = tenantsByName.get(key) ?? []
      const customers = customersByName.get(key) ?? []
      return {
        teamName,
        tenant: tenants.length === 1 ? tenants[0] : null,
        candidates: tenants.length > 1 ? tenants : [],
        suggestedCustomer:
          tenants.length === 0 && customers.length === 1 ? customers[0] : null,
      }
    })
  }
}
